import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserRole } from '../../data/model/user-role.model';
import { getCookie } from '@auth/bp-pf-auth';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RoleService {

  constructor() {
    this.setRoleFromAccessToken();
  }

  private roleSubject = new BehaviorSubject<UserRole>('agent');
  private rolesSubject = new BehaviorSubject<UserRole[]>([]);

  role$ = this.roleSubject.asObservable();
  roles$ = this.rolesSubject.asObservable();

  get role(): UserRole {
    return this.roleSubject.value;
  }

  get roles(): UserRole[] {
    return this.rolesSubject.value;
  }

  setRole(role: UserRole) {
    this.roleSubject.next(role);
    this.rolesSubject.next([role]);
  }

 hasRole(roles?: UserRole[]): boolean {
  if (!roles || roles.length === 0) {
    return false;
  }
  return roles.some(role => this.roles.includes(role));
}

  private setRoleFromAccessToken(): void {
    const token = this.getAccessToken();
    const roles = this.extractRolesFromToken(token);
    if (roles.length) {
      this.rolesSubject.next(roles);
      this.roleSubject.next(roles[0]);
    }
  }

  private getAccessToken(): string | null {
    const tenant = getCookie(`${environment.app_id}_tenant_code`);
    if (!tenant) return null;
    return getCookie(`${tenant}_${environment.app_id}_access_token`);
  }

  private extractRolesFromToken(token: string | null): UserRole[] {
    if (!token) return [];
    const payload = this.decodeJwtPayload(token);
    if (!payload) return [];

    const roleClaimKey = Object.keys(payload).find(key => key.endsWith('/roles'));
    const roles = roleClaimKey ? payload[roleClaimKey] : [];
    const rawRoles = Array.isArray(roles) ? roles : [roles];

    return rawRoles
      .map(role => this.normalizeRole(role))
      .filter((role): role is UserRole => !!role);
  }

  private normalizeRole(value: unknown): UserRole | null {
    if (!value) return null;
    const normalized = String(value).toLowerCase();
    if (normalized === 'agent') return 'agent';
    if (normalized === 'uw_assist' || normalized === 'uw_assistant') return 'uw_assist';
    if (normalized === 'underwriter') return 'underwriter';
    return null;
  }

  private decodeJwtPayload(token: string): Record<string, any> | null {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    const payload = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    try {
      const decoded = atob(payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, '='));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

}
